import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/shared/integrations/supabase/client";
import type { AttributeField } from "@/features/properties/types/propertyAttributes";
import { fetchResolvedSurveyAttributes } from "@/features/properties/hooks/usePropertyAttributes";
import { exportDeficiencies } from "@/features/exports/services";
import { toast } from "@/shared/hooks/use-toast";
import {
  PropertyReportGalleryView,
  type ReportGalleryItem,
} from "@/features/properties/components/PropertyReportGalleryView";
import { useAuditDeficiencies } from "@/features/audit/useAuditDeficiencies";
import { AuditFindingDetails } from "@/features/audit/AuditFindingDetails";
import { recommendationsForDeficiency, deficiencyKey } from "@/features/audit/normalizeAudit";
import { RuleCatalogDrawer } from "@/features/audit/RuleCatalogDrawer";
import type { RawDeficiency } from "@/features/properties/components/propertyOverview/deficiencyHelpers";

type DeficiencyExportFormat = "csv" | "xlsx";

type Severity = "critical" | "high" | "medium" | "low" | string;

interface DeficiencyPhoto {
  id: string;
  url: string;
  document_name: string;
}

interface DeficiencyItem extends RawDeficiency {
  photos: DeficiencyPhoto[];
}

interface PropertyDeficienciesGalleryProps {
  clientId: string;
  propertyId: string;
  propertyName: string;
}


function resolveAssetPhotoUrl(filePath: string | null | undefined): string {
  if (!filePath) return "/placeholder.svg";
  if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
    return filePath;
  }
  const { data } = supabase.storage.from("asset-documents").getPublicUrl(filePath);
  return data?.publicUrl || "/placeholder.svg";
}

function collectSurveyFields(fields: AttributeField[]): Map<string, AttributeField> {
  const fieldsById = new Map<string, AttributeField>();

  const walk = (nodes: AttributeField[]) => {
    for (const field of nodes) {
      fieldsById.set(field.id, field);
      if (field.type === "node") {
        walk(field.fields);
      }
    }
  };

  walk(fields);
  return fieldsById;
}

function getSurveyFieldIds(deficiency: RawDeficiency): string[] {
  const ids = new Set<string>();
  if (deficiency.survey_field_id) ids.add(deficiency.survey_field_id);
  // additional_survey_field_ids only exists on the legacy blob shape (not the
  // shared RawDeficiency); read it defensively.
  const additional = (deficiency as { additional_survey_field_ids?: unknown })
    .additional_survey_field_ids;
  if (Array.isArray(additional)) {
    additional.forEach((id) => {
      if (typeof id === "string" && id.length > 0) ids.add(id);
    });
  }
  return [...ids];
}

function getDeficiencyTitle(deficiency: RawDeficiency): string {
  return deficiency.title || deficiency.asset?.name || deficiency.survey_field || "Deficiency";
}

function getSeverityLabel(severity: Severity | null | undefined): string {
  switch ((severity ?? "").toLowerCase()) {
    case "critical":
      return "Critical";
    case "high":
      return "High";
    case "medium":
      return "Medium";
    case "low":
      return "Low";
    default:
      return "Unknown";
  }
}

function getSeverityDotClassName(severity: Severity | null | undefined): string {
  switch ((severity ?? "").toLowerCase()) {
    case "critical":
      return "bg-red-500";
    case "high":
      return "bg-orange-500";
    case "medium":
      return "bg-yellow-500";
    case "low":
      return "bg-blue-500";
    default:
      return "bg-muted-foreground";
  }
}

function getSeverityOrder(severity: Severity | null | undefined): number {
  switch ((severity ?? "").toLowerCase()) {
    case "critical":
      return 0;
    case "high":
      return 1;
    case "medium":
      return 2;
    case "low":
      return 3;
    default:
      return 4;
  }
}

function getGroupLabel(group: string | null | undefined): string {
  if (!group) return "Other";
  return group
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function getGroupOrder(group: string | null | undefined): number {
  const label = getGroupLabel(group).toLowerCase();
  if (label === "survey") return 0;
  if (label === "equipment") return 1;
  return 999;
}

async function hydratePhotos(
  propertyId: string,
  deficiencies: RawDeficiency[],
): Promise<DeficiencyItem[]> {
  if (deficiencies.length === 0) return [];

  const assetIds = [
    ...new Set(
      deficiencies
        .map((deficiency) => deficiency.asset?.asset_id)
        .filter((assetId): assetId is string => Boolean(assetId))
    ),
  ];
  const surveyFieldIds = new Set(deficiencies.flatMap(getSurveyFieldIds));

  const [assetPhotosResult, attributesResult] = await Promise.all([
    assetIds.length > 0
      ? supabase
          .from("asset_documents")
          .select("id, asset_id, document_name, file_path")
          .is("deleted_at", null)
          .eq("document_type", "photo")
          .in("asset_id", assetIds)
          .order("created_at", { ascending: false })
      : Promise.resolve({ data: [], error: null }),
    surveyFieldIds.size > 0
      ? fetchResolvedSurveyAttributes(propertyId).then((fields) => ({ data: fields, error: null }))
      : Promise.resolve({ data: [] as AttributeField[], error: null }),
  ]);

  if (assetPhotosResult.error) throw assetPhotosResult.error;
  if (attributesResult.error) throw attributesResult.error;

  const photosByAssetId = new Map<string, DeficiencyPhoto[]>();
  (assetPhotosResult.data ?? []).forEach((photo) => {
    const photos = photosByAssetId.get(photo.asset_id) ?? [];
    photos.push({
      id: photo.id,
      url: resolveAssetPhotoUrl(photo.file_path),
      document_name: photo.document_name || "Asset photo",
    });
    photosByAssetId.set(photo.asset_id, photos);
  });

  const surveyFields = (attributesResult.data ?? []) as AttributeField[];
  const surveyFieldsById = Array.isArray(surveyFields)
    ? collectSurveyFields(surveyFields)
    : new Map<string, AttributeField>();

  return deficiencies.map((deficiency) => {
    const assetId = deficiency.asset?.asset_id;
    if (assetId) {
      return {
        ...deficiency,
        photos: photosByAssetId.get(assetId) ?? [],
      };
    }

    const photos = getSurveyFieldIds(deficiency).flatMap((fieldId) => {
      const field = surveyFieldsById.get(fieldId);
      const evidence = (field as { evidence?: unknown } | undefined)?.evidence;
      if (!Array.isArray(evidence)) return [];

      return evidence
        .filter((url): url is string => typeof url === "string" && url.length > 0)
        .map((url, index) => ({
          id: `${deficiency.id}-${fieldId}-${index}`,
          url,
          document_name: `${field?.title || deficiency.survey_field || "Evidence"} ${index + 1}`,
        }));
    });

    return {
      ...deficiency,
      photos,
    };
  });
}

export function PropertyDeficienciesGallery({
  clientId,
  propertyId,
  propertyName,
}: PropertyDeficienciesGalleryProps) {
  const [exportingFormat, setExportingFormat] = useState<DeficiencyExportFormat | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const selectedDeficiencyId = searchParams.get("selectedDeficiencyId");
  const {
    deficiencies: rawDefs,
    recommendations,
    source,
    isLoading: defsLoading,
    error,
    refetch,
  } = useAuditDeficiencies(propertyId);
  const { data: deficiencies = [], isLoading: photosLoading, error: photosError } = useQuery({
    queryKey: [
      "deficiency-photos",
      propertyId,
      rawDefs.map((d) => d.asset?.asset_id ?? d.survey_field_id ?? d.id).join(","),
    ],
    enabled: rawDefs.length > 0,
    queryFn: () => hydratePhotos(propertyId, rawDefs),
  });
  const isLoading = defsLoading || photosLoading;
  const loadError = error ?? photosError;

  const items = useMemo<ReportGalleryItem[]>(() => {
    return deficiencies.map((deficiency) => ({
      id: deficiency.id ?? deficiencyKey(deficiency),
      name: getDeficiencyTitle(deficiency),
      category: getGroupLabel(deficiency.group),
      categoryOrder: getGroupOrder(deficiency.group),
      photos: deficiency.photos,
      navDotClassName: getSeverityDotClassName(deficiency.severity),
      navSubcategory: getSeverityLabel(deficiency.severity),
      navSubcategoryOrder: getSeverityOrder(deficiency.severity),
      details: [
        deficiency.group
          ? { key: "group", label: "Group", value: getGroupLabel(deficiency.group) }
          : null,
        deficiency.severity
          ? { key: "severity", label: "Severity", value: getSeverityLabel(deficiency.severity) }
          : null,
        deficiency.detail
          ? { key: "detail", label: "Detail", value: deficiency.detail }
          : null,
        deficiency.recommendation_summary
          ? { key: "recommendation", label: "Recommendation", value: deficiency.recommendation_summary }
          : null,
        deficiency.asset?.name
          ? { key: "asset", label: "Asset", value: deficiency.asset.name }
          : null,
        deficiency.asset?.category
          ? { key: "asset_category", label: "Asset Category", value: deficiency.asset.category }
          : null,
        deficiency.survey_field
          ? { key: "survey_field", label: "Survey Field", value: deficiency.survey_field }
          : null,
      ].filter((row): row is { key: string; label: string; value: string } => row !== null),
      extraContent:
        source === "audit" ? (
          <AuditFindingDetails
            deficiency={deficiency}
            recommendations={recommendationsForDeficiency(deficiency, recommendations)}
          />
        ) : undefined,
    }));
  }, [deficiencies, source, recommendations]);

  const summary = useMemo(() => {
    const severityCounts = deficiencies.reduce<Record<string, number>>((acc, deficiency) => {
      const label = getSeverityLabel(deficiency.severity);
      acc[label] = (acc[label] ?? 0) + 1;
      return acc;
    }, {});
    const recommendationCount = deficiencies.filter((deficiency) =>
      Boolean(deficiency.recommendation_summary?.trim())
    ).length;
    const topGroups = Object.entries(
      deficiencies.reduce<Record<string, number>>((acc, deficiency) => {
        const label = getGroupLabel(deficiency.group);
        acc[label] = (acc[label] ?? 0) + 1;
        return acc;
      }, {})
    )
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);

    return {
      severityCounts,
      recommendationCount,
      topGroups,
    };
  }, [deficiencies]);

  const handleExportDeficiencies = async (format: DeficiencyExportFormat) => {
    setExportingFormat(format);
    try {
      await exportDeficiencies({
        projectId: clientId,
        propertyId,
        label: propertyName || "Property",
        format,
      });
      toast({ title: `Deficiencies exported as ${format.toUpperCase()}` });
    } catch (err) {
      toast({
        title: "Export failed",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setExportingFormat(null);
    }
  };

  return (
    <>
      <PropertyReportGalleryView
        items={items}
      loading={isLoading}
      error={loadError}
      onRetry={refetch}
      searchPlaceholder="Search deficiencies..."
      emptyNavMessage="No deficiencies reported yet."
      showItemsWithoutPhotos
      detailTitle="Deficiency Details"
      detailEmptyMessage="Select a deficiency to view details."
      focusedItemId={selectedDeficiencyId}
      downloadAllLabel={exportingFormat ? "Exporting..." : "Export deficiencies"}
      downloadAllDisabled={exportingFormat !== null || deficiencies.length === 0}
      downloadAllOptions={[
        {
          label: "Export CSV",
          onClick: () => handleExportDeficiencies("csv"),
          disabled: exportingFormat !== null,
        },
        {
          label: "Export XLSX",
          onClick: () => handleExportDeficiencies("xlsx"),
          disabled: exportingFormat !== null,
        },
      ]}
      summary={
        deficiencies.length > 0 ? (
          <div className="rounded-lg border border-border bg-card/80 px-4 py-3">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Deficiencies
                  </p>
                  <p className="text-xl font-semibold leading-none text-foreground">
                    {deficiencies.length}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(summary.severityCounts).map(([severity, count]) => (
                    <span
                      key={severity}
                      className="rounded-full border border-border bg-muted/30 px-2 py-0.5 text-[11px] text-muted-foreground"
                    >
                      {severity}: {count}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex min-w-0 items-center gap-3 md:justify-end">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Recommendations
                  </p>
                  <p className="text-xl font-semibold leading-none text-foreground">
                    {summary.recommendationCount}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5 md:justify-end">
                  {summary.topGroups.length > 0 ? (
                    summary.topGroups.map(([group, count]) => (
                      <span
                        key={group}
                        className="rounded-full border border-border bg-muted/30 px-2 py-0.5 text-[11px] text-muted-foreground"
                      >
                        {group}: {count}
                      </span>
                    ))
                  ) : (
                    <span className="text-[11px] text-muted-foreground">No groups</span>
                  )}
                </div>
              </div>
            </div>
            {source === "audit" && (
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={() => setRulesOpen(true)}
                  className="text-xs font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                >
                  View rules
                </button>
              </div>
            )}
          </div>
        ) : null
      }
      />
      <RuleCatalogDrawer open={rulesOpen} onOpenChange={setRulesOpen} />
    </>
  );
}
